import React, { useState, useEffect, useMemo } from 'react';
import { Briefcase, Upload, CheckCircle, ArrowLeft, Search, MapPin, Building2, ChevronRight, X } from 'lucide-react';

interface Position {
  id: string;
  name: string;
  department: string;
  description: string;
  location: string;
  salary_range: string;
  requirements: string;
  employment_type?: string;   // intern=实习岗，收集「可实习时长」
}

interface CareerData {
  company: string;
  positions: Position[];
}

export default function CareerPage() {
  const [data, setData] = useState<CareerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [showTracker, setShowTracker] = useState(false);
  const [trackInput, setTrackInput] = useState('');
  const [trackResult, setTrackResult] = useState<any>(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [showRecover, setShowRecover] = useState(false);
  const [recoverInput, setRecoverInput] = useState('');
  const [recoverResult, setRecoverResult] = useState<any>(null);
  const [recoverError, setRecoverError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDept, setActiveDept] = useState('全部');

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [availableDate, setAvailableDate] = useState('');
  const [internDuration, setInternDuration] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const isInternPosition = selectedPosition?.employment_type === 'intern';

  // Extract token and optional positionId from URL
  // List page:   /career/:token
  // Detail page: /career/:token/:positionId
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const token = pathParts[1] || '';
  const positionIdFromPath = pathParts[2] || '';

  // SEO: set document title / meta tags for the public careers page
  useEffect(() => {
    const company = data?.company || '留形科技';
    const setMeta = (selector: string, attr: string, name: string, content: string) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    if (selectedPosition) {
      document.title = `${selectedPosition.name} - ${company}招聘官网`;
      const desc = `${company} · ${selectedPosition.department || ''} | ${(selectedPosition.description || '').replace(/\n/g, ' ').substring(0, 120)}...`;
      setMeta('meta[name="description"]', 'name', 'description', desc);
      setMeta('meta[property="og:title"]', 'property', 'og:title', `${selectedPosition.name} - ${company}招聘官网`);
      setMeta('meta[property="og:description"]', 'property', 'og:description', desc);
    } else {
      document.title = `留形招聘 | ${company}招聘官网 - 加入留形，先"形"一步`;
      const desc = `${company}（留形）招聘官网，正在招聘 ${data?.positions?.length ?? ''} 个职位。加入留形，先"形"一步——查看最新职位、在线投递简历、查询投递进度。`;
      setMeta('meta[name="description"]', 'name', 'description', desc);
      setMeta('meta[property="og:title"]', 'property', 'og:title', `留形招聘 | ${company}招聘官网`);
      setMeta('meta[property="og:description"]', 'property', 'og:description', desc);
    }
    setMeta('meta[name="keywords"]', 'name', 'keywords', '留形招聘,留形科技招聘,留形科技,Manifold,Manifold Tech,招聘,职位,投递简历,加入留形');
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMeta('meta[property="og:url"]', 'property', 'og:url', window.location.href);
  }, [data, selectedPosition]);

  useEffect(() => {
    if (!token) { setError('无效的链接'); setLoading(false); return; }

    if (positionIdFromPath) {
      // Detail page: load single position directly from URL (shareable link)
      fetch(`/api/career/${token}/${positionIdFromPath}`)
        .then(r => { if (!r.ok) throw new Error('岗位不存在或已关闭'); return r.json(); })
        .then(pos => {
          setSelectedPosition(pos);
          setData({ company: pos.company || '留形科技', positions: [] });
          setLoading(false);
          // Pre-fill referral code from ?ref= query param
          const ref = new URLSearchParams(window.location.search).get('ref');
          if (ref) setReferrerName(ref);
        })
        .catch(e => { setError(e.message); setLoading(false); });
    } else {
      // List page: load all positions
      fetch(`/api/career/${token}`)
        .then(r => { if (!r.ok) throw new Error('页面不存在'); return r.json(); })
        .then(d => {
          setData(d);
          setLoading(false);
          // Legacy: auto-select from ?pos= query param (redirect to new URL)
          const posId = new URLSearchParams(window.location.search).get('pos');
          if (posId && d.positions) {
            window.location.replace(`/career/${token}/${posId}`);
          }
        })
        .catch(e => { setError(e.message); setLoading(false); });
    }
  }, [token, positionIdFromPath]);

  const submitLock = React.useRef(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPosition || !name.trim() || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setSubmitError('');

    // Get ref from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref') || '';

    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('phone', phone.trim());
    formData.append('email', email.trim());
    if (ref) formData.append('ref', ref);
    if (referrerName.trim()) formData.append('referrer_name', referrerName.trim());
    if (availableDate) formData.append('available_date', availableDate);
    if (isInternPosition && internDuration) formData.append('intern_duration', internDuration);
    if (resumeFile) formData.append('resume', resumeFile);

    try {
      const res = await fetch(`/api/career/${token}/${selectedPosition.id}/apply`, {
        method: 'POST',
        body: formData,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message);
      setTrackingCode(result.tracking_code || '');
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message || '提交失败，请稍后重试');
      submitLock.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const departments = useMemo(() => {
    if (!data?.positions) return [];
    const depts = Array.from(new Set(data.positions.map(p => p.department).filter(Boolean)));
    return ['全部', ...depts];
  }, [data]);

  const filteredPositions = useMemo(() => {
    if (!data?.positions) return [];
    return data.positions.filter(p => {
      const matchDept = activeDept === '全部' || p.department === activeDept;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || p.name.toLowerCase().includes(q) || (p.department || '').toLowerCase().includes(q) || (p.location || '').toLowerCase().includes(q);
      return matchDept && matchSearch;
    });
  }, [data, activeDept, searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
          <p className="text-gray-400 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <p className="text-gray-400 text-lg">{error}</p>
      </div>
    );
  }

  const handleTrack = async () => {
    if (!trackInput.trim()) return;
    setTrackLoading(true);
    setTrackError('');
    setTrackResult(null);
    try {
      const res = await fetch(`/api/career/track/${trackInput.trim().toUpperCase()}`);
      const result = await res.json();
      if (!res.ok) throw new Error(result.message);
      setTrackResult(result);
    } catch (err: any) {
      setTrackError(err.message || '查询失败');
    } finally {
      setTrackLoading(false);
    }
  };

  const handleRecover = async () => {
    if (!recoverInput.trim()) return;
    setRecoverError('');
    setRecoverResult(null);
    try {
      const input = recoverInput.trim();
      const body = input.includes('@') ? { email: input } : { phone: input };
      const res = await fetch('/api/career/track/recover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message);
      setRecoverResult(result);
    } catch (err: any) {
      setRecoverError(err.message || '查询失败');
    }
  };

  const STEP_LABELS = ['已投递', '简历评审', '面试中', 'Offer', '入职'];

  const Navbar = ({ onTrack }: { onTrack: () => void }) => (
    <nav style={{ background: '#111' }} className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-6 sm:px-10">
      <div className="flex-1 flex items-center gap-2.5">
        <img src="/p2-logo.jpg" alt={data?.company || '留形科技'} className="h-8 w-8 rounded-full object-cover" />
        <span className="text-white font-semibold text-base tracking-wide">留形招聘</span>
      </div>
      <div className="flex items-center gap-5 sm:gap-7">
        <a href="https://www.manifoldtech.cn/" target="_blank" rel="noopener noreferrer"
          className="text-white/70 font-medium text-sm hover:text-white transition-colors">回到官网</a>
        <button onClick={onTrack} className="text-white font-medium text-sm hover:opacity-80 transition-opacity">查询投递进度</button>
      </div>
    </nav>
  );

  // Progress tracking page
  if (showTracker) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onTrack={() => {}} />
        <div className="pt-14 min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="w-full max-w-md">
            <button onClick={() => { setShowTracker(false); setTrackResult(null); setTrackError(''); }}
              className="flex items-center gap-1 text-gray-400 hover:text-gray-800 mb-6 text-sm transition-colors">
              <ArrowLeft size={15} /> 返回
            </button>
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-1">查询投递进度</h2>
              <p className="text-sm text-gray-400 mb-6">输入投递时获得的查询码</p>
              <div className="flex gap-2 mb-3">
                <input value={trackInput} onChange={e => setTrackInput(e.target.value)}
                  placeholder="查询码" maxLength={8}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-center font-mono text-lg tracking-widest uppercase focus:outline-none focus:border-gray-900 transition-colors" />
                <button onClick={handleTrack} disabled={trackLoading || !trackInput.trim()}
                  style={{ background: '#111' }} className="px-5 py-2.5 text-white rounded-lg font-medium text-sm hover:opacity-80 disabled:opacity-40 transition-opacity">
                  {trackLoading ? '...' : '查询'}
                </button>
              </div>
              <button onClick={() => setShowRecover(!showRecover)}
                className="text-xs text-gray-400 hover:text-gray-700 underline mb-4">
                忘记查询码？用手机号 / 邮箱找回
              </button>
              {showRecover && (
                <div className="border border-gray-100 rounded-xl p-4 mb-4 bg-gray-50">
                  <p className="text-sm text-gray-500 mb-2">输入投递时填写的手机号或邮箱</p>
                  <input value={recoverInput} onChange={e => setRecoverInput(e.target.value)}
                    placeholder="手机号或邮箱"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-gray-900 transition-colors" />
                  <button onClick={handleRecover} disabled={!recoverInput.trim()}
                    style={{ background: '#111' }} className="w-full py-2 text-white rounded-lg text-sm font-medium hover:opacity-80 disabled:opacity-40">
                    找回查询码
                  </button>
                  {recoverResult && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-400">您的查询码</p>
                      <p className="text-xl font-bold text-gray-900 font-mono tracking-widest">{recoverResult.tracking_code}</p>
                      <p className="text-xs text-gray-400 mt-1">{recoverResult.name} · {recoverResult.position_name}</p>
                    </div>
                  )}
                  {recoverError && <p className="text-red-500 text-xs mt-2">{recoverError}</p>}
                </div>
              )}
              {trackError && <p className="text-red-500 text-sm mb-3">{trackError}</p>}
              {trackResult && (
                <div className="border border-gray-100 rounded-xl p-5 mt-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-base font-bold text-gray-900">{trackResult.name}</p>
                      <p className="text-sm text-gray-400">{trackResult.position_name}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      trackResult.step === -1 ? 'bg-red-50 text-red-600' : 'bg-gray-900 text-white'
                    }`}>{trackResult.status}</span>
                  </div>
                  {trackResult.step > 0 && (
                    <div className="flex items-center gap-1">
                      {STEP_LABELS.map((label, idx) => (
                        <React.Fragment key={idx}>
                          <div className="flex flex-col items-center flex-1">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                              idx + 1 <= trackResult.step ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'
                            }`}>{idx + 1}</div>
                            <span className={`text-[9px] mt-1 whitespace-nowrap ${idx + 1 <= trackResult.step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{label}</span>
                          </div>
                          {idx < STEP_LABELS.length - 1 && (
                            <div className={`h-px flex-1 -mt-3 ${idx + 1 < trackResult.step ? 'bg-gray-900' : 'bg-gray-200'}`} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-4">投递时间: {trackResult.applied_at?.replace('T', ' ').slice(0, 16)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Submitted success
  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onTrack={() => setShowTracker(true)} />
        <div className="pt-14 min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-10 max-w-md w-full text-center shadow-sm">
            <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">投递成功</h2>
            <p className="text-gray-400 text-sm">我们会尽快查看您的简历，如合适会及时与您联系。</p>
            {trackingCode && (
              <div className="mt-6 p-5 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-xs text-gray-400 mb-2">您的投递查询码</p>
                <p className="text-3xl font-bold text-gray-900 font-mono tracking-[0.2em]">{trackingCode}</p>
                <p className="text-xs text-gray-400 mt-2">请妥善保存，可凭此码随时查询投递进度</p>
              </div>
            )}
            <button
              onClick={() => { window.location.href = `/career/${token}`; }}
              className="mt-8 text-sm text-gray-400 hover:text-gray-700 transition-colors"
            >
              ← 返回职位列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Application form
  if (selectedPosition) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onTrack={() => setShowTracker(true)} />
        <div className="pt-14 min-h-screen bg-gray-50">
          <div className="max-w-2xl mx-auto px-4 py-10">
            <a href={`/career/${token}`}
              className="flex items-center gap-1.5 text-gray-400 hover:text-gray-800 text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> 返回全部职位
            </a>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-7 border-b border-gray-100">
                <h1 className="text-2xl font-bold text-gray-900">{selectedPosition.name}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-400">
                  {selectedPosition.department && <span className="flex items-center gap-1"><Building2 size={13} />{selectedPosition.department}</span>}
                  {selectedPosition.location && <span className="flex items-center gap-1"><MapPin size={13} />{selectedPosition.location}</span>}
                </div>
              </div>
              {(selectedPosition.description || selectedPosition.requirements) && (
                <div className="p-7 border-b border-gray-100">
                  {selectedPosition.description && (
                    <div className="mb-5">
                      <h3 className="font-semibold text-gray-900 mb-2 text-xs uppercase tracking-widest text-gray-400">岗位描述</h3>
                      <p className="text-gray-500 text-sm whitespace-pre-line leading-relaxed">{(selectedPosition.description || '').replace(/[\n\r]*【[紧急程度优先级]+[】：:].*/g, '')}</p>
                    </div>
                  )}
                  {selectedPosition.requirements && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-xs uppercase tracking-widest text-gray-400">任职要求</h3>
                      <p className="text-gray-500 text-sm whitespace-pre-line leading-relaxed">{selectedPosition.requirements}</p>
                    </div>
                  )}
                </div>
              )}
              <form onSubmit={handleSubmit} className="p-7 space-y-5">
                <h3 className="font-bold text-gray-900 text-lg">投递简历</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">姓名 *</label>
                    <input type="text" required value={name} onChange={e => setName(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors" placeholder="请输入姓名" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">手机号</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors" placeholder="请输入手机号" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">邮箱</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors" placeholder="请输入邮箱" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">内推码</label>
                    <input type="text" value={referrerName} onChange={e => setReferrerName(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors" placeholder="如有内推码请填写" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">到岗时间</label>
                    <input type="month" value={availableDate} onChange={e => setAvailableDate(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors text-gray-700" />
                  </div>
                  {isInternPosition && (
                    <div>
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">可实习时长</label>
                      <select value={internDuration} onChange={e => setInternDuration(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 transition-colors text-gray-700 bg-white">
                        <option value="">请选择</option>
                        <option value="3个月以内">3个月以内</option>
                        <option value="3-6个月">3-6个月</option>
                        <option value="6-12个月">6-12个月</option>
                        <option value="12个月以上">12个月以上</option>
                        <option value="不定/可长期">不定 / 可长期</option>
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">简历附件</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-gray-400 transition-colors cursor-pointer group"
                    onClick={() => document.getElementById('resume-input')?.click()}>
                    <Upload className="w-5 h-5 text-gray-300 group-hover:text-gray-500 mx-auto mb-2 transition-colors" />
                    {resumeFile
                      ? <p className="text-sm text-gray-900 font-medium">{resumeFile.name}</p>
                      : <p className="text-sm text-gray-400">点击上传简历 <span className="text-gray-300">PDF / DOC / DOCX，最大 10MB</span></p>}
                  </div>
                  <input id="resume-input" type="file" className="hidden" accept=".pdf,.doc,.docx"
                    onChange={e => setResumeFile(e.target.files?.[0] || null)} />
                </div>
                {submitError && <p className="text-red-500 text-sm">{submitError}</p>}
                <button type="submit" disabled={submitting || !name.trim()}
                  style={{ background: '#111' }} className="w-full text-white py-3 rounded-xl font-semibold text-sm hover:opacity-80 disabled:opacity-40 transition-opacity">
                  {submitting ? '提交中...' : '提交投递'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Position list — DJI style
  return (
    <div className="min-h-screen bg-white">
      <Navbar onTrack={() => setShowTracker(true)} />

      {/* Hero */}
      <div className="pt-14">
        <div
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(/career-hero.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          className="relative overflow-hidden px-6 sm:px-10 py-20 sm:py-32 text-white"
        >
          <div className="max-w-5xl mx-auto relative z-10 text-center">
            <h1 className="text-3xl sm:text-5xl font-bold leading-tight drop-shadow-lg">加入留形，先“形”一步</h1>
          </div>
        </div>
      </div>

      {/* Search bar — full width row */}
      <div className="sticky top-14 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-10 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 w-4 h-4" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="搜索职位名称、部门、城市..."
              className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 transition-colors" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main: left sidebar + right list */}
      <div className="max-w-5xl mx-auto px-4 sm:px-10 py-8 flex gap-8 items-start">
        {/* Left sidebar — department filter */}
        {departments.length > 1 && (
          <aside className="w-44 flex-shrink-0 hidden sm:block">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">部门</p>
            <ul className="space-y-1">
              {departments.map(dept => (
                <li key={dept}>
                  <button onClick={() => setActiveDept(dept)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeDept === dept
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}>
                    {dept}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Right — position list */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-400 mb-5">找到 <span className="font-semibold text-gray-900">{filteredPositions.length}</span> 个职位</p>
          {filteredPositions.length === 0 ? (
            <div className="text-center py-20">
              <Briefcase className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">暂无符合条件的职位</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {filteredPositions.map(pos => (
                <a key={pos.id} href={`/career/${token}/${pos.id}`}
                  className="block px-6 py-5 hover:bg-gray-50 transition-colors group flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 mb-1.5">{pos.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      {pos.department && <span className="flex items-center gap-1"><Building2 size={11} />{pos.department}</span>}
                      {pos.location && <span className="flex items-center gap-1"><MapPin size={11} />{pos.location}</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 flex-shrink-0 transition-colors" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <footer className="border-t border-gray-100 mt-10 py-8 text-center">
        <p className="text-xs text-gray-300">Powered by 留形面试官</p>
      </footer>
    </div>
  );
}
